import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export enum DateGranularity {
  Hour = 'hour',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year',
}

export interface UnitSuggestion {
  recommended: DateGranularity;
  available: DateGranularity[];
}

@Injectable({
  providedIn: 'root'
})
export class UnitSuggestionService {

  constructor() { }

  getUnitSuggestions(startDate: string, endDate: string): UnitSuggestion {
    const allUnits = [
      { unit: DateGranularity.Hour, name: 'Hourly' },
      { unit: DateGranularity.Day, name: 'Daily' },
      { unit: DateGranularity.Week, name: 'Weekly' },
      { unit: DateGranularity.Month, name: 'Monthly' },
      { unit: DateGranularity.Quarter, name: 'Quarterly' },
      { unit: DateGranularity.Year, name: 'Yearly' },
    ];

    // Use dayjs to get timestamps for comparison
    const startTime = dayjs.utc(startDate).valueOf();
    const endTime = dayjs.utc(endDate).valueOf();

    if (isNaN(startTime) || isNaN(endTime)) {
      return { recommended: DateGranularity.Day, available: allUnits.map((u) => u.unit) };
    }

    const diffMs = Math.abs(endTime - startTime);
    const oneDay = 86400000;
    const sevenDays = oneDay * 7;
    const oneMonth = 2629800000;
    const threeMonths = oneMonth * 3;
    const sixMonths = oneMonth * 6;
    const oneYear = oneDay * 365; // Approx

    let recommended: DateGranularity = DateGranularity.Day;
    let available: DateGranularity[] = [];

    if (diffMs <= oneDay) {
      recommended = DateGranularity.Hour;
      available = [DateGranularity.Hour];
    } else if (diffMs <= sevenDays) {
      recommended = DateGranularity.Day;
      available = [DateGranularity.Day, DateGranularity.Hour];
    } else if (diffMs <= oneMonth) {
      recommended = DateGranularity.Week;
      available = [DateGranularity.Day, DateGranularity.Week];
    } else if (diffMs <= threeMonths) {
      recommended = DateGranularity.Month;
      available = [DateGranularity.Month, DateGranularity.Week];
    } else if (diffMs <= sixMonths) {
      recommended = DateGranularity.Month;
      available = [DateGranularity.Month, DateGranularity.Week];
    } else if (diffMs <= oneYear) {
      recommended = DateGranularity.Month;
      available = [DateGranularity.Month, DateGranularity.Quarter];
    } else {
      recommended = DateGranularity.Year;
      available = [DateGranularity.Quarter, DateGranularity.Year];
    }

    // Ensure all available units are valid DateGranularity values
    const validUnits = allUnits.map(u => u.unit);
    available = available.filter(u => validUnits.includes(u));

    return {
      recommended,
      available,
    };
  }
}
